/*
 * Jira Ticket:
 * Created Date: Fri, 21st Feb 2020, 09:53:18 am
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2020 The Distance
 */

import React, { Fragment, useEffect, useState } from 'react';
import {
  fetchStart,
  fetchEnd,
  Loading,
  Title,
  useDataProvider,
  useNotify,
} from 'react-admin';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { StatisticsCard } from 'Components';
import * as Icons from '@material-ui/icons';
import { dashboardStyles, centeredStyle } from 'styles';
import { orange } from 'theme';
import { isApprover } from 'utils/roles';

const selectIcon = statistic => Icons[statistic.icon];

const DashboardStatisticsCards = ({ statistics, classes }) => {
  const columnLength = Math.ceil(statistics.length / 2);
  const firstColumnComponents = [];
  const secondColumnComponents = [];
  for (let i = 0; i < statistics.length; i++) {
    const statistic = statistics[i];
    const icon = selectIcon(statistic);
    if (i < columnLength) {
      firstColumnComponents.push(
        <div
          className={classes.singleCol}
          key={statistic.name + statistic.value + String(icon)}
        >
          <StatisticsCard
            icon={icon}
            bgColour={orange}
            value={statistic.value}
            description={statistic.name}
          />
        </div>
      );
    } else {
      secondColumnComponents.push(
        <div
          className={classes.singleCol}
          key={statistic.name + statistic.value + String(icon)}
        >
          <StatisticsCard
            key={statistic.name}
            icon={icon}
            bgColour={orange}
            value={statistic.value}
            description={statistic.name}
          />
        </div>
      );
    }
  }
  return (
    <Fragment>
      <div className={classes.leftCol}>{firstColumnComponents}</div>
      <div className={classes.rightCol}>{secondColumnComponents}</div>
    </Fragment>
  );
};

const Dashboard = props => {
  const classes = dashboardStyles();
  const centeredClasses = centeredStyle();
  const dataProvider = useDataProvider();
  const dispatch = useDispatch();
  const history = useHistory();
  const [statistics, setStatistics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const notify = useNotify();
  const dashboardProps = { statistics, classes };
  const { userPermissions } = props;
  const isApproved = isApprover(userPermissions);

  useEffect(() => {
    let isSubscribed = true;
    const getDashboardStatistics = () => {
      const userSub = localStorage.getItem('userSub');
      const initialAppViewDone = localStorage.getItem('initialAppViewDone');

      if (!initialAppViewDone && isApproved) {
        history.push('/inspection');
        localStorage.setItem('initialAppViewDone', '1');
      }
      if (userSub) {
        dispatch(fetchStart());
        // array called statistics = [ icon, name, value ]
        return dataProvider('GET_STATISTICS', 'Dashboard', {})
          .then(result => {
            dispatch(fetchEnd());
            if (isSubscribed) {
              setStatistics(result.data.statistics);
              setIsLoading(false);
            }
          })
          .catch(error => {
            dispatch(fetchEnd());
            if (error.graphQLErrors.length > 0) {
              // only return an error if it's graphql error
              setIsLoading(false);
              return notify('error.dashboard.unableToGetStats', 'warning');
            }
          });
      }
    };
    getDashboardStatistics();
    return () => (isSubscribed = false);
  }, [
    dispatch,
    dataProvider,
    notify,
    setStatistics,
    setIsLoading,
    history,
    isApproved,
  ]);

  return (
    <div className={classes.flex}>
      <Title title={'resources.Dashboard.title'} />
      {!isLoading ? (
        statistics.length > 0 ? (
          <DashboardStatisticsCards {...dashboardProps} />
        ) : null
      ) : (
        <Loading
          className={centeredClasses.root}
          loadingPrimary="ra.page.loading"
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  userPermissions: JSON.parse(localStorage.getItem('org_permissions')),
});

export default connect(
  mapStateToProps,
  {} // Avoid connect passing dispatch in props
)(Dashboard);
